
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { generateAnonymousId } from '@/utils/anonymousId';
import { supabase } from '@/integrations/supabase/client';
import { debugVoteFlow } from '@/utils/debug';

export const useAnonymousVoting = (pollId?: string, isAuthenticated: boolean = false) => {
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const [isAnonymousAllowed, setIsAnonymousAllowed] = useState<boolean>(false);
  const [isCheckingAnonymousVote, setIsCheckingAnonymousVote] = useState(false);
  const [hasAnonymousVoted, setHasAnonymousVoted] = useState(false);

  // Generate anonymous ID if needed - use standardized approach
  useEffect(() => {
    const getAnonymousId = async () => {
      if (!isAuthenticated) {
        try {
          const id = await generateAnonymousId();
          setAnonymousId(id);
          debugVoteFlow('ANONYMOUS_VOTING_ID_SET', { anonymousId: id });
        } catch (error) {
          debugVoteFlow('ANONYMOUS_VOTING_ID_ERROR', { error });
        }
      }
    };

    getAnonymousId();
  }, [isAuthenticated]);

  // Check if anonymous user has already voted
  useEffect(() => {
    const checkAnonymousVote = async () => {
      if (anonymousId && pollId && !isAuthenticated) {
        setIsCheckingAnonymousVote(true);
        try {
          debugVoteFlow('CHECKING_ANONYMOUS_VOTE_STATUS', { pollId, anonymousId });
          
          // Use RPC function instead of direct query
          const { data, error } = await supabase.rpc('check_anonymous_vote', {
            poll_id: pollId,
            anonymous_id: anonymousId
          });
          
          if (error) {
            debugVoteFlow('ANONYMOUS_VOTE_CHECK_ERROR', { error });
            throw error;
          }
          
          const hasVoted = !!data;
          setHasAnonymousVoted(hasVoted);
          debugVoteFlow('ANONYMOUS_VOTE_STATUS_RESULT', { hasVoted, data });
        } catch (error) {
          debugVoteFlow('ANONYMOUS_VOTE_CHECK_EXCEPTION', { error });
          console.error('Error checking anonymous vote:', error);
          // On error, assume they haven't voted to allow retry
          setHasAnonymousVoted(false);
        } finally {
          setIsCheckingAnonymousVote(false);
        }
      } else {
        setIsCheckingAnonymousVote(false);
      }
    };

    checkAnonymousVote();
  }, [anonymousId, pollId, isAuthenticated]);

  // Track if poll allows anonymous voting
  useEffect(() => {
    if (pollId) {
      setIsAnonymousAllowed(true); // Default to true, will be updated when poll data is available
    }
  }, [pollId]);

  // Update isAnonymousAllowed when poll data changes
  const updateAnonymousAllowed = (allowed: boolean) => {
    setIsAnonymousAllowed(allowed);
    debugVoteFlow('ANONYMOUS_ALLOWED_STATUS_UPDATED', { allowed });
  };

  // Submit anonymous vote
  const submitAnonymousVote = async (optionId: number) => {
    if (!pollId || !anonymousId) {
      debugVoteFlow('ANONYMOUS_VOTE_SUBMIT_BLOCKED', { 
        pollId: !!pollId, 
        anonymousId: !!anonymousId 
      });
      return false;
    }
    
    try {
      debugVoteFlow('SUBMITTING_ANONYMOUS_VOTE_DIRECT', { pollId, optionId, anonymousId });
      
      // Use RPC function instead of direct insert
      const { error } = await supabase.rpc('insert_anonymous_vote', {
        poll_id: pollId,
        option_id: optionId,
        anonymous_id: anonymousId
      });
      
      if (error) {
        debugVoteFlow('ANONYMOUS_VOTE_SUBMIT_ERROR', { error });
        throw error;
      }
      
      // Update local state
      setHasAnonymousVoted(true);
      debugVoteFlow('ANONYMOUS_VOTE_SUBMITTED_SUCCESS', { pollId, optionId });
      return true;
    } catch (error) {
      debugVoteFlow('ANONYMOUS_VOTE_SUBMIT_EXCEPTION', { error });
      console.error('Error submitting anonymous vote:', error);
      return false;
    }
  };

  return {
    anonymousId,
    isAnonymousAllowed,
    hasAnonymousVoted,
    isCheckingAnonymousVote,
    updateAnonymousAllowed,
    submitAnonymousVote
  };
};
